import { Link } from 'react-router-dom';
import type { TenantUsage } from '@sellokart/shared';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { fetchPlatformUsage, usageKeys } from '@/features/plans/api';

/** The dimensions this tenant has exceeded, as a readable clause. */
function reasons(usage: TenantUsage): string {
  const parts: string[] = [];
  if (usage.overBills) {
    parts.push(
      `${usage.avgBillsPerDay.toFixed(1)} bills/day vs ${usage.plan?.maxBillsPerDay}`,
    );
  }
  if (usage.overBranches) {
    parts.push(`${usage.branchCount} branches vs ${usage.plan?.maxBranches}`);
  }
  if (usage.overDevices) {
    parts.push(`${usage.deviceCount} tills vs ${usage.plan?.maxDevices}`);
  }
  return parts.join(', ');
}

/**
 * Over-limit tenants, surfaced where an SA actually lands.
 *
 * This console has no separate dashboard — the tenants list IS the landing
 * page — so the notification lives here rather than on a page built solely to
 * hold it. Deliberately renders NOTHING when nobody is over: a banner that is
 * always present is a banner nobody reads, and the whole value of this one is
 * that its appearance means something.
 *
 * It also stays silent while loading and on error. Usage is supporting
 * information on this page, and an error box about a measurement would sit
 * above the tenant list the operator actually came for. The Plans page and each
 * tenant's own usage panel both report their failures properly.
 */
export function OverLimitAlert() {
  const query = useQuery({
    queryKey: usageKeys.platform(),
    queryFn: ({ signal }) => fetchPlatformUsage(signal),
  });

  const over = query.data?.tenants.filter((row) => row.isOverLimit) ?? [];
  if (over.length === 0) return null;

  return (
    <Alert
      tone="warning"
      title={`${over.length} tenant${over.length === 1 ? '' : 's'} over plan`}
      className="mb-4"
    >
      <p>
        Measured over the last {query.data?.windowDays ?? 30} days. Limits warn
        only — these tills are still trading.
      </p>
      <ul className="mt-2 space-y-1">
        {over.map((row) => (
          <li key={row.tenantId}>
            <Link
              to={`/cp/tenants/${row.tenantId}`}
              className="font-medium underline underline-offset-2"
            >
              {row.tenantName}
            </Link>
            <span className="text-amber-800">
              {' '}
              — {row.plan?.name}: {reasons(row)}
            </span>
          </li>
        ))}
      </ul>
    </Alert>
  );
}
